import {
  Badge,
  confirm,
  createSortableColumn,
  DataTable,
  toast,
} from "@devhop/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  CopyIcon,
  EditIcon,
  EyeIcon,
  TrashIcon,
  XIcon,
} from "lucide-react";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { formatDateTime } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";
import { flowsApi } from "../api/client";
import { flowsKeys, useDeleteFlow } from "../api/queries";
import { statusColors, statusLabels } from "../utils/labels";

type FlowRow = {
  id: string;
  title: string;
  description: string | null;
  purpose: string | null;
  departmentId: string | null;
  departmentName: string | null;
  status: "active" | "draft" | "inactive";
  createdAt: string;
  updatedAt: string;
  positionTags?: Array<{
    positionId: string;
    positionName: string;
    departmentName?: string;
  }>;
};

type FlowsTableProps = {
  data: FlowRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onEdit: (flow: FlowRow) => void;
  onView?: (id: string) => void;
  onCopy?: (id: string) => void;
};

export function FlowsTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onEdit,
  onView,
  onCopy,
}: FlowsTableProps) {
  const { has } = usePermissions();
  const deleteFlow = useDeleteFlow();
  const qc = useQueryClient();

  const deactivateFlowMutation = useMutation({
    mutationFn: ({ id, currentData }: { id: string; currentData: FlowRow }) =>
      flowsApi.update(id, {
        title: currentData.title,
        description: currentData.description ?? undefined,
        purpose: currentData.purpose ?? undefined,
        departmentId: currentData.departmentId ?? undefined,
        status: "inactive",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: flowsKeys.all });
      toast.success("ປິດການໃຊ້ງານ Flow ສໍາເລັດ");
    },
    onError: () => {
      toast.error("ປິດການໃຊ້ງານ Flow ລົ້ມເຫຼວ");
    },
  });

  const activateFlowMutation = useMutation({
    mutationFn: ({ id, currentData }: { id: string; currentData: FlowRow }) =>
      flowsApi.update(id, {
        title: currentData.title,
        description: currentData.description ?? undefined,
        purpose: currentData.purpose ?? undefined,
        departmentId: currentData.departmentId ?? undefined,
        status: "active",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: flowsKeys.all });
      toast.success("ເປີດການໃຊ້ງານ Flow ສໍາເລັດ");
    },
    onError: () => {
      toast.error("ເປີດການໃຊ້ງານ Flow ລົ້ມເຫຼວ");
    },
  });

  const handleDelete = async (flow: FlowRow) => {
    const confirmed = await confirm({
      title: "ລຶບ Flow",
      description: `ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລຶບ "${flow.title}"?`,
      actionText: "ຢືນຢັນ",
      cancelText: "ຍົກເລີກ",
    });
    if (!confirmed) return;

    try {
      await deleteFlow.run(flow.id);
      toast.success("ລຶບ Flow ສໍາເລັດ");
    } catch {
      toast.error("ລຶບ Flow ລົ້ມເຫຼວ");
    }
  };

  const handleDeactivate = async (flow: FlowRow) => {
    const confirmed = await confirm({
      title: "ປິດການໃຊ້ງານ Flow",
      description: `ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການປິດການໃຊ້ງານ "${flow.title}"?`,
      actionText: "ຢືນຢັນ",
      cancelText: "ຍົກເລີກ",
    });
    if (!confirmed) return;

    deactivateFlowMutation.mutate({ id: flow.id, currentData: flow });
  };

  const handleActivate = async (flow: FlowRow) => {
    const confirmed = await confirm({
      title: "ເປີດການໃຊ້ງານ Flow",
      description: `ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການເປີດການໃຊ້ງານ "${flow.title}"?`,
      actionText: "ຢືນຢັນ",
      cancelText: "ຍົກເລີກ",
    });
    if (!confirmed) return;

    activateFlowMutation.mutate({ id: flow.id, currentData: flow });
  };

  const handleCopy = async (flow: FlowRow) => {
    if (!onCopy) return;
    onCopy(flow.id);
  };

  const columns: import("@devhop/ui").TanstackReactTable.ColumnDef<FlowRow>[] =
    [
      createSortableColumn<FlowRow>("title", "ຊື່ Flow", {
        size: 200,
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="font-medium">{row.original.title}</span>
            {row.original.description && (
              <span className="text-muted-foreground text-xs">
                {row.original.description}
              </span>
            )}
          </div>
        ),
      }),
      {
        id: "departmentName",
        header: "ພະແນກ",
        cell: ({ row }) => (
          <span className="text-sm">{row.original.departmentName || "-"}</span>
        ),
        size: 150,
      },
      {
        id: "positionTags",
        header: "Tags",
        cell: ({ row }) => (
          <div className="flex max-w-xs flex-wrap gap-1">
            {row.original.positionTags?.slice(0, 3).map((tag) => (
              <Badge key={tag.positionId} variant="outline" className="text-xs">
                {tag.positionName}
              </Badge>
            ))}
            {row.original.positionTags &&
              row.original.positionTags.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{row.original.positionTags.length - 3}
                </Badge>
              )}
          </div>
        ),
        size: 150,
      },
      {
        id: "status",
        header: "ສະຖານະ",
        cell: ({ row }) => (
          <Badge variant={statusColors[row.original.status] || "secondary"}>
            {statusLabels[row.original.status] || row.original.status}
          </Badge>
        ),
        size: 100,
      },
      createSortableColumn<FlowRow>("updatedAt", "ວັນທີ່ອັບເດດ", {
        size: 150,
        cell: ({ row }) => (
          <span className="text-muted-foreground text-sm">
            {formatDateTime(row.original.updatedAt)}
          </span>
        ),
      }),
      {
        size: 100,
        id: "actions",
        cell: ({ row }: { row: { original: FlowRow } }) => {
          const flow = row.original;
          const canUpdate = has(Permissions.flows.update);
          const canDelete = has(Permissions.flows.delete);

          const actions: {
            label: string;
            icon?: React.ReactNode;
            variant?: "destructive";
            onClick: () => void;
          }[] = [];

          if (onView) {
            actions.push({
              label: "ເບິ່ງ",
              icon: <EyeIcon className="h-4 w-4" />,
              onClick: () => onView(flow.id),
            });
          }

          if (canUpdate) {
            actions.push({
              label: "ແກ້ໄຂ",
              icon: <EditIcon className="h-4 w-4" />,
              onClick: () => onEdit(flow),
            });
          }

          if (canUpdate && onCopy) {
            actions.push({
              label: "ຄັດລອກ",
              icon: <CopyIcon className="h-4 w-4" />,
              onClick: () => handleCopy(flow),
            });
          }

          if (canUpdate && flow.status === "active") {
            actions.push({
              label: "ປິດການໃຊ້ງານ",
              variant: "destructive",
              icon: <XIcon className="h-4 w-4" />,
              onClick: () => handleDeactivate(flow),
            });
          }

          if (canUpdate && flow.status === "draft") {
            actions.push({
              label: "ເປີດການໃຊ້ງານ",
              icon: <EyeIcon className="h-4 w-4" />,
              onClick: () => handleActivate(flow),
            });
          }

          if (canUpdate && flow.status === "inactive") {
            actions.push({
              label: "ເປີດການໃຊ້ງານ",
              icon: <CheckCircle2 className="h-4 w-4" />,
              onClick: () => handleActivate(flow),
            });
          }

          if (canDelete) {
            actions.push({
              label: "ລຶບ",
              variant: "destructive",
              icon: <TrashIcon className="h-4 w-4" />,
              onClick: () => handleDelete(flow),
            });
          }

          return <RowActions actions={actions} maxInline={2} />;
        },
      },
    ];

  return (
    <DataTable<FlowRow, unknown>
      noDataMessage="ບໍ່ພົບ Flow"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
    />
  );
}
